<script setup lang="ts">
/** Step 1 · Reference (plan §3 step 1): photos, product page, published text, analysis. */
import { computed, ref, watch } from 'vue';
import Icon from './Icon.vue';
import StepPanel from './StepPanel.vue';
import PanelHead from './PanelHead.vue';
import { ui } from '@/app/state';
import { addPhotos, ANALYSIS_STAGES, ANALYSIS_UNAVAILABLE, cancelAnalysis, chooseStyle, DEMO_UNRECORDED, removePhoto, requestAnalysis, RIGHTS_BASES, runAnalysis, startManual, projectHasValues } from '@/app/analysis';
import { api, type DemoPhoto } from '@/app/api';
import { isFitResult } from '@/families/fit-schema';
import { go } from '@/app/navigation';
import { scheduleSave } from '@/app/persistence';
import { highlightPieces, recogniseFacts } from '@/app/facts';
import { fieldKeyOf } from '@/app/intake';
import { fmtDate } from '@/app/format';
import { labelOf } from '@/app/navigation';

const a = computed(() => ui.analysis);
const r = computed(() => ui.ref);
const draft = (id: string, fallback: string) => (id in ui.drafts ? ui.drafts[id]! : fallback);
const onInput = (e: Event) => { const t = e.target as HTMLInputElement; ui.drafts[t.id] = t.value; };

const facts = computed(() => {
  const text = r.value.text;
  const p = a.value.proposal;
  const spans = (p ? (isFitResult(p) ? p.evidence.textSpans : p.evidence.textSpans) : []) ?? [];
  const fromProposal = spans.filter((s) => text.slice(s.span[0], s.span[1]) === s.text).map((s) => ({ field: fieldKeyOf(s.kind) ?? s.kind, span: s.span as [number, number], text: s.text }));
  return fromProposal.length ? fromProposal : recogniseFacts(text);
});
const pieces = computed(() => highlightPieces(r.value.text, facts.value));
const stageIdx = computed(() => (a.value.stage === 'done' ? 4 : a.value.stageIndex ?? 0));
const done = computed(() => ['done', 'review', 'manual'].includes(a.value.status) && !ui.empty);
const statusLine = computed(() => {
  const when = a.value.status === 'manual' ? 'Set up by hand' : `Analysed ${fmtDate(a.value.ranAt)}`;
  return `${when} · ${a.value.status === 'review' ? 'proposal waiting for your check' : 'values checked and accepted'}`;
});

/** Fixture intake: nothing on this computer can read a new photo (only recorded samples answer); in a demo, a photo that is not one of the prepared ones. */
const unavailable = computed(() => a.value.status === 'error' && (a.value.code === ANALYSIS_UNAVAILABLE || a.value.code === DEMO_UNRECORDED));
const offline = computed(() => ui.service.intakeMode === 'fixture' && !ui.service.demo && a.value.status !== 'running' && !unavailable.value);

/** Demo mode: prepared photos, each with a recorded reading; one click adds the photo and analyses it. */
const demo = ref<DemoPhoto[]>([]);
watch(() => ui.service.demo, async (on) => { demo.value = on ? await api.demoPhotos() : []; }, { immediate: true });
const tryingDemo = ref<string | null>(null);
async function tryDemo(p: DemoPhoto) {
  if (tryingDemo.value) return;
  tryingDemo.value = p.id;
  try {
    const blob = await (await fetch(p.url)).blob();
    const ext = blob.type === 'image/webp' ? 'webp' : blob.type === 'image/png' ? 'png' : 'jpg';
    // the exact product text the reading was recorded with, so its published sizes apply
    ui.ref.text = p.text;
    scheduleSave();
    await addPhotos([new File([blob], `${p.id}.${ext}`, { type: blob.type })], RIGHTS_BASES[1], { replace: true });
    if (ui.ref.images.length) requestAnalysis();
  } finally {
    tryingDemo.value = null;
  }
}

/** FND-19: one sentence per part, each ending in exactly one full stop. */
const sentence = (t: string) => { const x = t.trim().replace(/[\s.]+$/, ''); return x ? `${x}.` : ''; };
/** The service's reason, with any doubled full stop from joined sentences removed. */
const refusalReason = computed(() => (a.value.refusal?.reason ?? '').replace(/(?<!\.)\.\s*\.(?!\.)/g, '.'));
const refusalDetail = computed(() => [
  a.value.refusal?.detected?.family ? sentence(`Seen in the photo: ${a.value.refusal.detected.family}`) : '',
  'No sizes or shelf positions were measured or guessed.',
  projectHasValues() ? 'Your current project is unchanged.' : '',
].filter(Boolean).join(' '));
function commitUrl(e: Event) { ui.ref.url = (e.target as HTMLInputElement).value; delete ui.drafts.refUrl; scheduleSave(); }
function commitText(e: Event) { ui.ref.text = (e.target as HTMLTextAreaElement).value; delete ui.drafts.refText; scheduleSave(); }
function openPicker() { ui.pickerOpen = true; }
</script>

<template>
  <StepPanel>
    <PanelHead :n="1" title="Your reference" lede="The bookcase you’d like to make your own. Photos, the product page and its published text are the evidence for every proposed value." />

    <section v-if="a.status === 'running'" class="card analysis" aria-label="Analysis progress">
      <div class="an-head">
        <span class="spinner" aria-hidden="true" /><strong>Analysing your reference</strong>
        <button type="button" class="btn btn--sm btn--quiet" data-act="cancel-analysis" @click="cancelAnalysis">Cancel</button>
      </div>
      <ol class="stages">
        <li v-for="(s, i) in ANALYSIS_STAGES" :key="s.id" :class="{ 'is-done': i < stageIdx, 'is-now': i === stageIdx }">
          <span class="st-mark"><Icon v-if="i < stageIdx" name="check" /></span>{{ s.label }}
        </li>
      </ol>
      <p v-if="a.notice" class="an-notice small" role="status" data-notice="still-analysing"><Icon name="clock" /><span>{{ a.notice }}</span></p>
      <p class="muted small">{{ ui.service.intakeMode === 'fixture' ? 'Fixture analysis: a recorded response, no call leaves this Mac.' : 'The photos and text go to the analysis on the local service. The result arrives as a proposal for you to check.' }}</p>
    </section>
    <section v-else-if="a.status === 'refused'" class="card refusal" aria-labelledby="refTitle" data-nofit>
      <h2 id="refTitle" class="h-card"><Icon name="alert" /> We can’t rebuild this one yet</h2>
      <p data-refusal-reason>{{ refusalReason }}</p>
      <p class="muted small">
        {{ refusalDetail }}
      </p>
      <div class="row-acts">
        <button type="button" class="btn btn--ink" data-act="manual" @click="chooseStyle">Set up by hand from a style</button>
        <button type="button" class="btn btn--quiet" data-act="pick" @click="openPicker">Choose other photos</button>
      </div>
    </section>
    <section v-else-if="a.status === 'error'" class="card refusal" aria-labelledby="errTitle" :data-error="a.code">
      <h2 id="errTitle" class="h-card"><Icon name="alert" /> {{ a.code === DEMO_UNRECORDED ? 'Not one of the prepared photos' : unavailable ? 'Photo analysis is offline' : 'The analysis stopped' }}</h2>
      <p>{{ a.error }}</p>
      <div class="row-acts">
        <button v-if="!unavailable" type="button" class="btn btn--ink" data-act="analyse" @click="runAnalysis({ mode: a.mode ?? 'replace' })">Try again</button>
        <button type="button" class="btn" :class="unavailable ? 'btn--ink' : 'btn--quiet'" data-act="manual" @click="startManual()">Set up by hand as a new project</button>
      </div>
    </section>
    <section v-else-if="a.status === 'idle' || ui.empty" class="card empty-card">
      <h2 class="h-card">Start from something you love</h2>
      <p class="muted">Add two or three photos of a bookcase or shelf, its product page and the published text. We’ll recognise its style and propose a layout for you to check.</p>
    </section>
    <template v-else>
      <p class="status-line"><Icon name="check" /><span>{{ statusLine }}</span></p>
      <p class="muted small replace-note"><Icon name="history" /><span>Analysing again replaces your current values; undo restores them. You can also start a new project from new photos.</span></p>
    </template>
    <p v-if="offline" class="muted small replace-note" data-note="intake-offline"><Icon name="laptop" /><span>Photo analysis is offline on this computer.</span></p>

    <section v-if="demo.length && !r.images.length && a.status !== 'running'" class="sec demo-photos" aria-labelledby="demoH" data-demo-photos>
      <h2 id="demoH" class="h-sec">Try a prepared photo</h2>
      <p class="muted small">Each has a recorded analysis, so it is quick and the same every time.</p>
      <ul class="demo-grid">
        <li v-for="p in demo" :key="p.id">
          <button type="button" class="demo-tile" :data-demo="p.id" :aria-busy="tryingDemo === p.id ? 'true' : undefined" :disabled="!!tryingDemo" @click="tryDemo(p)">
            <img :src="p.url" :alt="''" loading="lazy">
            <span>{{ p.title }}</span>
          </button>
        </li>
      </ul>
    </section>
    <section class="sec" aria-labelledby="photosH">
      <div class="sec-head">
        <h2 id="photosH" class="h-sec">Photos</h2>
        <button type="button" class="btn btn--sm btn--quiet" data-act="pick" @click="openPicker"><Icon name="image" /> Add or change</button>
      </div>
      <ul v-if="r.images.length" class="thumbs">
        <li v-for="(im, i) in r.images" :key="im.id" class="thumb" :class="{ 'thumb--lead': i === 0 }">
          <img :src="im.thumb" :alt="im.alt" loading="lazy" @error="($event.target as HTMLImageElement).src = im.src">
          <span class="thumb-n">{{ i + 1 }}</span>
          <button type="button" class="thumb-x" :data-remove="im.id" :aria-label="`Remove photo ${i + 1}`" @click="removePhoto(im.id)"><Icon name="close" /></button>
        </li>
      </ul>
      <button v-else type="button" class="dropzone" data-act="pick" @click="openPicker">
        <Icon name="image" /><span><strong>Add photos</strong><small>Front, angle and a close-up work best</small></span>
      </button>
    </section>
    <section class="sec">
      <label class="lbl" for="refUrl"><Icon name="link" /> Product page</label>
      <input id="refUrl" class="input" type="url" inputmode="url" placeholder="https://" autocomplete="off" :value="draft('refUrl', r.url)" @input="onInput" @change="commitUrl">
    </section>
    <section class="sec">
      <label class="lbl" for="refText"><Icon name="text" /> Published product text</label>
      <textarea id="refText" class="input textarea" rows="3" placeholder="Paste the description, sizes and materials" :value="draft('refText', r.text)" @input="onInput" @change="commitText" />
      <template v-if="r.text && facts.length">
        <p class="facts" aria-label="Published facts found in the text">
          <template v-for="(p, i) in pieces" :key="i"><mark v-if="p.field" :data-fact="p.field" :title="labelOf(p.field)">{{ p.text }}</mark><template v-else>{{ p.text }}</template></template>
        </p>
        <p class="muted small">Highlighted: facts the analysis reads as <em>published</em>.</p>
      </template>
    </section>

    <template #dock>
      <template v-if="a.status === 'running'">
        <p class="dock-note">{{ a.stageLabel || 'Starting' }}…</p>
        <button type="button" class="btn btn--quiet" data-act="cancel-analysis" @click="cancelAnalysis">Cancel</button>
      </template>
      <template v-else>
        <button type="button" class="btn" :class="done ? 'btn--quiet' : 'btn--ink btn--lg'" data-act="analyse" @click="requestAnalysis"><Icon name="spark" /> {{ done ? 'Analyse again' : 'Analyse reference' }}</button>
        <button v-if="done" type="button" class="btn btn--ink btn--lg" data-go="check" @click="go('check')">Check the values <Icon name="arrow" /></button>
      </template>
    </template>
  </StepPanel>
</template>

<style scoped>
.demo-grid { list-style: none; margin: 8px 0 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 10px; }
.demo-tile { display: grid; gap: 6px; width: 100%; padding: 6px; border: 1px solid var(--line); border-radius: 12px; background: var(--card); color: var(--ink-2); font: inherit; font-size: 12px; line-height: 1.3; text-align: left; cursor: pointer; }
.demo-tile:hover:not(:disabled) { border-color: var(--line-2); background: var(--card-2); }
.demo-tile:disabled { cursor: progress; opacity: .6; }
.demo-tile[aria-busy="true"] { opacity: 1; border-color: var(--honey); }
.demo-tile img { width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 8px; background: var(--sunk); }
</style>
